import React, { useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames/bind';
import { AppConstants, SlowFormUrl } from '../../utils/constants';
import { storeData, storeFormData } from '../../utils/sessionStorage';
import EnrollmentContext from '../../contexts/EnrollmentContext';
import { trackFormError, trackFormSubmission } from '../../utils/monitoring';
import { getSegmentId } from '../../utils/segment';
import {
  pardotFormSubmission,
  attachFormValue,
  serializePardotData,
  submitAllDataToSlowForm,
} from '../../utils/browserUtils';
import arrowForward from '../../images/arrow-forward.svg';
import EnrollmentFormHeader from './EnrollmentFormHeader';
import EnrollmentFormError from '../FormInputs/FormError';
import ButtonLoader from '../ButtonLoader';
import styles from '../../styles/EnrollmentForm.module.scss';

const cx = classNames.bind(styles);

const EnrollmentForm = ({
  formName,
  callbackLocations,
  submitText,
  submitId,
  actionUrl,
  validationHandler,
  formValid,
  preSubmit,
  asyncPreSubmit,
  saveState,
  children,
  slowSubmit,
}) => {
  const history = useHistory();
  const { currentPage } = AppConstants;

  const { userEmail, page, setCurrentPage, maxPage, slowForm } = useContext(EnrollmentContext);

  const formRef = useRef();
  const useSlowForm = slowForm || slowSubmit || false;

  // On a re-render, the default state passed to useState
  // does not get reset: the value stays as the current
  // state value. Navigating back using history.push
  // acts as a re-render, so we must explicitly
  // set serverError vs. passing serverDidError in
  // to the default value of the useState() call.
  const { serverDidError, errorMessage } = history.location.state || {};
  const [serverError, setServerError] = useState(false);
  if (!serverError && serverDidError) {
    setServerError(true);
  }
  const [submitInProgress, setSubmitInProgress] = useState(false);

  const storeCurrentPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    storeData(currentPage, pageNumber);
  };

  const submitSlowFormData = async (data) => {
    // Always include segment_id in slow form submission
    data.segment_id = await getSegmentId();
    // Ensure all form data is present as an <input> and matches
    // the inputs value for slow form submission.
    const fieldset = formRef.current.getElementsByTagName('fieldset')[0];
    for (const [field, value] of Object.entries(data)) {
      const input = fieldset.elements[field];
      if (input && input.type !== 'file') {
        input.value = value;
      } else {
        attachFormValue(fieldset, field, value);
      }
    }
    // pause for analytics
    // without this, the analytics in trackFormSubmission don't register
    setTimeout(() => formRef.current.submit(), 1500);
  };

  const pardotFallback = (err, res) => {
    if (err || !res || !res.success) {
      trackFormError({
        err,
        res,
        formName,
      });
      // Retrieves all cached data and performs a slow submit
      submitAllDataToSlowForm(page, formName, submitText);
    }
  };

  const submitFormData = (data) => {
    storeFormData(formName, { ...saveState, ...data });
    storeCurrentPage(page + 1);
    const message = useSlowForm ? 'Enrollment Portal SlowForm Submitted' : 'Enrollment Portal Form Submitted';
    trackFormSubmission({
      data,
      pageName: formName,
      pageNumber: page,
      message,
      submitText,
    }).then(() => {
      if (useSlowForm) {
        submitSlowFormData(data);
      } else {
        // fastform submission
        pardotFormSubmission(formName, actionUrl, callbackLocations, data, pardotFallback);
        history.push(`/${page + 1}`, { from: window.location.href });
      }
    });
  };

  const onFormSubmit = (data) => {
    if (submitInProgress) {
      return;
    }
    setSubmitInProgress(true);
    setServerError(false);
    // All pardot submissions require users' email address.
    data.email = userEmail;
    // For preSubmit handlers that perform an asynchonous task,
    // the asyncPreSubmit prop must be set on this component
    // and the preSubmit function must return a promise that is
    // resolved when the asynchronous task is complete.
    if (useSlowForm && asyncPreSubmit && preSubmit) {
      preSubmit(data)
        .then((newData) => submitFormData(serializePardotData(newData)))
        .catch(() => {
          history.push(`/s/${page}`, { serverDidError: true, from: window.location.href });
        });
    } else {
      if (preSubmit) {
        preSubmit(data);
      }
      submitFormData(serializePardotData(data));
    }
  };

  return (
    <form
      ref={formRef}
      method="GET"
      action={SlowFormUrl}
      className={styles.EnrollmentForm}
      onSubmit={validationHandler(onFormSubmit)}
      noValidate
    >
      <EnrollmentFormHeader page={page} maxPage={maxPage} />
      <fieldset className={styles.EnrollmentFormFields}>
        {children}
        {serverError && (
          <EnrollmentFormError
            errorField={{ type: 'default' }}
            errorText={{
              default:
                errorMessage ||
                'An error occurred submitting this page. Please reload the page and try again in a few minutes.  If the problem persists, contact support@pathrise.com',
            }}
            blockError
          />
        )}
        <button id={submitId} type="submit" className={cx('formSubmit', { disabled: !formValid })}>
          {submitInProgress ? (
            <ButtonLoader />
          ) : (
            <>
              {submitText}
              <img className="iconRight" alt={submitText} src={arrowForward} />
            </>
          )}
        </button>
      </fieldset>
    </form>
  );
};

// asyncPreSubmit indicates that the preSubmit handler
// performs asynchronous actions, and needs to return
// a promise if using the slowForm.
EnrollmentForm.defaultProps = {
  submitText: 'Next',
  submitId: null,
  preSubmit: null,
  asyncPreSubmit: false,
  saveState: {},
  slowSubmit: false,
};

const callbackLocationsShape = {
  success_location: PropTypes.string.isRequired,
  error_location: PropTypes.string.isRequired,
};

EnrollmentForm.propTypes = {
  formName: PropTypes.string.isRequired,
  callbackLocations: PropTypes.shape(callbackLocationsShape).isRequired,
  submitText: PropTypes.string,
  submitId: PropTypes.string,
  actionUrl: PropTypes.string.isRequired,
  formValid: PropTypes.bool.isRequired,
  validationHandler: PropTypes.func.isRequired,
  preSubmit: PropTypes.func,
  asyncPreSubmit: PropTypes.bool,
  saveState: PropTypes.shape({}),
  children: PropTypes.node.isRequired,
  slowSubmit: PropTypes.bool,
};

export default EnrollmentForm;
